import React, { useState } from "react";
import {
  HiOutlineArrowRight,
  HiOutlineCheckCircle,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
  HiOutlineExclamationCircle,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineMinusCircle,
  HiOutlineShieldCheck,
  HiOutlineXCircle,
} from "react-icons/hi";
import {
  AI_CHECK_STATUS_STYLES,
  enrichAiCheck,
  formatAiTabLabel,
  isCriticalAiCheck,
  resolveAiCheckBadge,
  resolveDetailTabIdForCheck,
  triageAiChecks,
} from "../utils/timesheetAiUtils";

const GROUP_TONE = {
  red: {
    dot: "bg-red-500",
    label: "text-red-800",
    count: "bg-red-50 ring-1 ring-red-200 text-red-700",
    icon: HiOutlineXCircle,
    iconColor: "text-red-500",
  },
  amber: {
    dot: "bg-amber-500",
    label: "text-amber-900",
    count: "bg-amber-50 ring-1 ring-amber-200 text-amber-800",
    icon: HiOutlineExclamationCircle,
    iconColor: "text-amber-500",
  },
  emerald: {
    dot: "bg-emerald-500",
    label: "text-emerald-800",
    count: "bg-emerald-50 ring-1 ring-emerald-200 text-emerald-700",
    icon: HiOutlineCheckCircle,
    iconColor: "text-emerald-500",
  },
  gray: {
    dot: "bg-slate-400",
    label: "text-slate-700",
    count: "bg-slate-100 ring-1 ring-slate-200 text-slate-600",
    icon: HiOutlineMinusCircle,
    iconColor: "text-slate-400",
  },
};

const ACCENT_STYLES = {
  fail: {
    bar: "bg-gradient-to-b from-red-300 via-red-500 to-red-600",
    glow: "shadow-[inset_-1px_0_0_rgba(255,255,255,0.35)]",
    ring: "ring-red-100/60",
  },
  warn: {
    bar: "bg-gradient-to-b from-amber-300 via-amber-500 to-amber-600",
    glow: "shadow-[inset_-1px_0_0_rgba(255,255,255,0.35)]",
    ring: "ring-amber-100/60",
  },
  pass: {
    bar: "bg-gradient-to-b from-emerald-300 via-emerald-500 to-emerald-600",
    glow: "shadow-[inset_-1px_0_0_rgba(255,255,255,0.35)]",
    ring: "ring-emerald-100/50",
  },
  skipped: {
    bar: "bg-gradient-to-b from-slate-200 via-slate-300 to-slate-400",
    glow: "",
    ring: "ring-slate-100/50",
  },
};

const StatusAccent = ({ statusKey }) => {
  const accent = ACCENT_STYLES[statusKey] || ACCENT_STYLES.skipped;
  return (
    <span className="pointer-events-none absolute inset-y-0 left-0 w-[3px]" aria-hidden>
      <span
        className={`absolute inset-0 rounded-l-[inherit] ${accent.bar} ${accent.glow}`}
      />
      <span
        className={`absolute inset-y-1 left-0 w-[3px] rounded-full opacity-40 blur-[2px] ${accent.bar}`}
      />
    </span>
  );
};

const ScopeChip = ({ check }) => {
  const label = check?.participant_label
    ? check.participant_label
    : check?.level === "timesheet" || !check?.participant_index
      ? "Shift"
      : null;
  if (!label) return null;
  return (
    <span className="inline-flex max-w-[10rem] truncate rounded-md border border-slate-200/70 bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
      {label}
    </span>
  );
};

export const ReportCheckItem = ({
  check,
  showDetails = false,
  canNavigate = false,
  onGoToTab,
  muted = false,
}) => {
  const enriched = enrichAiCheck(check);
  const badge = resolveAiCheckBadge(enriched);
  const style =
    AI_CHECK_STATUS_STYLES[badge.statusKey] || AI_CHECK_STATUS_STYLES.skipped;
  const tabId = resolveDetailTabIdForCheck(enriched);
  const tabLabel = formatAiTabLabel(enriched?.tab);
  const status = String(enriched?.status || "skipped").toLowerCase();
  const evidence = (enriched?.evidence || []).filter(Boolean);
  const showNavigate =
    canNavigate && tabId && status !== "pass" && status !== "skipped";
  const isCritical = isCriticalAiCheck(enriched);
  const summaryLine = enriched?.summary || enriched?.description || "";
  const accentKey = badge.statusKey;

  if (!showDetails) {
    return (
      <article
        className={`relative overflow-hidden rounded-lg border bg-white py-2.5 pl-3.5 pr-3 shadow-sm ${
          muted ? "border-slate-100/90 bg-slate-50/30" : style.border
        } ${ACCENT_STYLES[accentKey]?.ring ? `ring-1 ring-inset ${ACCENT_STYLES[accentKey].ring}` : ""}`}
      >
        <StatusAccent statusKey={accentKey} />
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h4 className="text-[13px] font-semibold leading-snug text-slate-900">
              {enriched?.label}
            </h4>
            <span
              className={`rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase ${style.badge}`}
            >
              {badge.label}
            </span>
            <ScopeChip check={enriched} />
          </div>
          {summaryLine ? (
            <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-500">
              {summaryLine}
            </p>
          ) : null}
          {showNavigate ? (
            <button
              type="button"
              onClick={() => onGoToTab?.(tabId, tabLabel)}
              className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-[#F0F4FF] px-2 py-1 text-[11px] font-semibold text-[#4B6CB7] transition hover:bg-[#E2EBFF]"
            >
              Go to {tabLabel}
              <HiOutlineArrowRight className="h-3 w-3" />
            </button>
          ) : null}
        </div>
      </article>
    );
  }

  return (
    <article
      className={`relative overflow-hidden rounded-lg border bg-white p-3 shadow-sm ${
        muted ? "border-slate-100/90 bg-slate-50/30" : style.border
      } ${isCritical ? "ring-1 ring-inset ring-red-100/80" : ""}`}
    >
      <StatusAccent statusKey={accentKey} />
      <div className="min-w-0 pl-1">
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <h4 className="text-[13px] font-semibold text-slate-900">
            {enriched?.label}
          </h4>
          <span
            className={`rounded px-1.5 py-px text-[9px] font-bold uppercase ${style.badge}`}
          >
            {badge.label}
          </span>
          <ScopeChip check={enriched} />
        </div>
        {enriched?.description ? (
          <p className="mt-1 text-[11px] text-slate-400">{enriched.description}</p>
        ) : null}
        {enriched?.summary ? (
          <p className="mt-1.5 text-xs leading-relaxed text-slate-700">
            {enriched.summary}
          </p>
        ) : null}
        {evidence.length > 0 ? (
          <ul className="mt-2 space-y-1.5 rounded-lg border border-slate-100 bg-slate-50/80 px-2.5 py-2">
            {evidence.map((line) => (
              <li
                key={line}
                className="flex items-start gap-1.5 text-[11px] leading-snug text-slate-600"
              >
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-slate-300" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        ) : null}
        {enriched?.suggestion ? (
          <div className="mt-2 rounded-lg border border-blue-100 bg-gradient-to-br from-[#F5F8FF] to-white px-2.5 py-2">
            <p className="text-[9px] font-bold uppercase tracking-wide text-[#4B6CB7]">
              Suggested fix
            </p>
            <p className="mt-0.5 text-[11px] leading-snug text-[#02397A]">
              {enriched.suggestion}
            </p>
          </div>
        ) : null}
        {showNavigate ? (
          <button
            type="button"
            onClick={() => onGoToTab?.(tabId, tabLabel)}
            className="mt-2 inline-flex items-center gap-1 rounded-lg bg-[#4B6CB7] px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-sm transition hover:bg-[#3D5A9E]"
          >
            Go to {tabLabel}
            <HiOutlineArrowRight className="h-3 w-3" />
          </button>
        ) : null}
      </div>
    </article>
  );
};

const TriageSection = ({
  title,
  tone = "gray",
  count,
  children,
  defaultOpen = true,
  isLast = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const meta = GROUP_TONE[tone] || GROUP_TONE.gray;
  const Icon = meta.icon;

  if (!count) return null;

  return (
    <div className={isLast ? "" : "border-b border-slate-200/70"}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left transition hover:bg-slate-50/80"
      >
        <span
          className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-inset ring-slate-100`}
        >
          <Icon className={`h-3.5 w-3.5 ${meta.iconColor}`} />
        </span>
        <span className={`text-xs font-bold uppercase tracking-wide ${meta.label}`}>
          {title}
        </span>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums ${meta.count}`}
        >
          {count}
        </span>
        <span className="ml-auto inline-flex h-6 w-6 items-center justify-center rounded-md text-gray-400 ring-1 ring-inset ring-gray-300">
          {open ? (
            <HiOutlineChevronUp color="#4b5563" className="h-3.5 w-3.5" />
          ) : (
            <HiOutlineChevronDown color="#4b5563" className="h-3.5 w-3.5" />
          )}
        </span>
      </button>
      {open ? (
        <div className="space-y-2 border-t border-slate-100 bg-slate-50/40 px-3.5 pb-3 pt-2">
          {children}
        </div>
      ) : null}
    </div>
  );
};

export const ReportCheckTriage = ({
  checks = [],
  showDetails = false,
  canNavigate = false,
  onGoToTab,
  emptyMessage = "No checks in this report.",
}) => {
  const { critical, review, passed, skipped } = triageAiChecks(checks);
  const total = critical.length + review.length + passed.length + skipped.length;

  if (!total) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white px-4 py-8 text-center">
        <HiOutlineShieldCheck className="mb-1.5 h-6 w-6 text-slate-300" />
        <p className="text-xs text-slate-500">{emptyMessage}</p>
      </div>
    );
  }

  const sections = [
    { key: "critical", title: "Critical", tone: "red", items: critical, open: true },
    { key: "review", title: "Needs review", tone: "amber", items: review, open: true },
    { key: "passed", title: "Passed", tone: "emerald", items: passed, open: false, muted: true },
    { key: "skipped", title: "Not applicable", tone: "gray", items: skipped, open: false, muted: true },
  ].filter((s) => s.items.length > 0);

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm">
      {sections.map((section, index) => (
        <TriageSection
          key={section.key}
          title={section.title}
          tone={section.tone}
          count={section.items.length}
          defaultOpen={section.open}
          isLast={index === sections.length - 1}
        >
          {section.items.map((check) => (
            <ReportCheckItem
              key={`${section.key}-${check.id}-${check.participant_index ?? "ts"}`}
              check={check}
              showDetails={showDetails}
              canNavigate={canNavigate}
              onGoToTab={onGoToTab}
              muted={section.muted}
            />
          ))}
        </TriageSection>
      ))}
    </div>
  );
};

export const ShowDetailsToggle = ({ enabled, onChange, disabled = false }) => (
  <div
    className={`flex items-center justify-between gap-3 rounded-xl border px-3.5 py-2.5 shadow-sm transition ${
      enabled
        ? "border-[#93C5FD]/80 bg-gradient-to-r from-[#F0F4FF] to-white"
        : "border-slate-200/90 bg-white"
    }`}
  >
    <div className="flex min-w-0 items-center gap-2.5">
      <span
        className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
          enabled ? "bg-[#4B6CB7] text-white shadow-sm" : "bg-slate-100 text-slate-500"
        }`}
      >
        {enabled ? (
          <HiOutlineEye className="h-3.5 w-3.5" />
        ) : (
          <HiOutlineEyeOff className="h-3.5 w-3.5" />
        )}
      </span>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-slate-900">Show details</p>
        <p className="truncate text-[11px] text-slate-500">
          {enabled ? "Evidence & recommendations" : "Quick scan view"}
        </p>
      </div>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition-colors ${
        enabled ? "bg-[#4B6CB7]" : "bg-slate-200"
      } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
          enabled ? "translate-x-5" : "translate-x-1"
        }`}
      />
    </button>
  </div>
);
